import React, { useEffect, useMemo, useState } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';
import { useAuth } from '../hooks/useAuth';
import './ChooseLocation.css';

interface Location {
  id: number;
  name: string;
  city?: string | null;
  address_line1?: string | null;
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * Eenmalig scherm bij de eerste keer inloggen. Wie via Microsoft binnenkomt is
 * beheerder, maar nog van niets: hier wordt bepaald bij welke locatie diegene
 * hoort. Daarna kan alleen de superadmin die koppeling nog wijzigen.
 */
const ChooseLocation: React.FC = () => {
  const { user, logout, refreshProfile } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedLocation = useMemo(
    () => locations.find((location) => location.id === selectedId) || null,
    [locations, selectedId]
  );

  useEffect(() => {
    let cancelled = false;

    apiClient
      .get<ApiResponse<Location[]>>('/locations')
      .then((response) => {
        if (cancelled) return;
        setLocations(response.data.data || []);
      })
      .catch((requestError) => {
        if (cancelled) return;
        console.error('Kon de locaties niet laden:', requestError);
        setError('De locaties konden niet geladen worden. Probeer de pagina te vernieuwen.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const confirmChoice = async () => {
    if (!selectedLocation) return;

    setIsSaving(true);
    setError(null);

    try {
      await apiClient.post('/me/location', { location_id: selectedLocation.id });
      // Het profiel opnieuw ophalen zet needsLocationChoice uit, waarna het
      // beheerpaneel vanzelf verschijnt.
      await refreshProfile();
    } catch (requestError: any) {
      console.error('Kon de locatie niet opslaan:', requestError);
      const forbidden = permissionMessage(requestError);
      setError(
        forbidden ||
          requestError.response?.data?.error ||
          'De locatie kon niet opgeslagen worden. Probeer het opnieuw.'
      );
      setIsConfirming(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="choose-location">
        <div className="choose-location-card">
          <div className="choose-location-spinner" aria-hidden="true"></div>
          <p>Locaties laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="choose-location">
      <div className="choose-location-card">
        <h1>Bij welke locatie hoor jij?</h1>
        <p className="choose-location-intro">
          Welkom{user?.name ? ` ${user.name}` : ''}. Je logt voor het eerst in op de
          duofietsapplicatie. Kies hieronder de locatie waarvoor jij werkt. Daarna beheer je de
          fietsen en reserveringen van die locatie.
        </p>

        <div className="choose-location-notice">
          <span className="choose-location-notice-icon" aria-hidden="true">
            ℹ️
          </span>
          <span>
            Deze keuze kun je daarna niet zelf meer wijzigen. Klopt er iets niet, dan kan de
            superadmin je naar een andere locatie verplaatsen.
          </span>
        </div>

        {error && (
          <div className="choose-location-error" role="alert">
            {error}
          </div>
        )}

        {locations.length === 0 ? (
          <div className="choose-location-empty">
            <p>Er zijn nog geen locaties aangemaakt.</p>
            <p className="choose-location-help">
              Vraag de superadmin om eerst een locatie toe te voegen en log daarna opnieuw in.
            </p>
          </div>
        ) : (
          <div className="choose-location-list" role="radiogroup" aria-label="Locaties">
            {locations.map((location) => (
              <button
                key={location.id}
                type="button"
                role="radio"
                aria-checked={selectedId === location.id}
                className={`choose-location-option${
                  selectedId === location.id ? ' is-selected' : ''
                }`}
                onClick={() => setSelectedId(location.id)}
                disabled={isSaving}
              >
                <span className="choose-location-option-name">{location.name}</span>
                {(location.city || location.address_line1) && (
                  <span className="choose-location-option-meta">
                    {[location.address_line1, location.city].filter(Boolean).join(', ')}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {isConfirming && selectedLocation ? (
          <div className="choose-location-confirm">
            <p>
              Je wordt beheerder van <strong>{selectedLocation.name}</strong>
              {selectedLocation.city ? ` in ${selectedLocation.city}` : ''}. Weet je dat zeker?
            </p>
            <div className="choose-location-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsConfirming(false)}
                disabled={isSaving}
              >
                Terug
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmChoice}
                disabled={isSaving}
              >
                {isSaving ? 'Bezig met opslaan...' : 'Ja, dit is mijn locatie'}
              </button>
            </div>
          </div>
        ) : (
          <div className="choose-location-actions">
            <button type="button" className="btn btn-secondary" onClick={logout}>
              Uitloggen
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setIsConfirming(true)}
              disabled={!selectedLocation}
            >
              Bevestigen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChooseLocation;
